import React from 'react';
import PropTypes from 'prop-types';
import Link from 'gatsby-link';
import { css } from 'emotion';

import events from '../utils/events';
import { navItems, navBreakpoint } from '../utils/config';
import presets, { colors, animation } from '../utils/presets';
import { toRem } from '../utils/functions';

import MenuToggle from '../components/menu-toggle';

const { curve, speed } = animation;

const navToggle = css`
  color: ${colors.gray.copy};
  transition: color ${speed.default} ${curve.default};

  .dark-header .headroom--unfixed & {
    color: ${colors.ui.bright};
  }

  @media (min-width: ${navBreakpoint}px) {
    display: none;
  }
`;

const navItem = css`
  height: ${toRem(presets.baselineGrid * 4)};
  display: none;
  align-items: flex-end;
  margin: 0 ${toRem(presets.baselineGrid * 2)};

  @media (min-width: ${navBreakpoint}px) {
    display: flex;
  }
`;

const linksPadding = new Proxy(
  {
    'top|bottom': toRem(presets.baselineGrid / 4),
    'left|right': toRem(presets.baselineGrid / 2),
  },
  {
    get(target, property) {
      /* eslint-disable-next-line */
      for (const k in target) {
        if (new RegExp(k).test(property)) {
          return target[k];
        }
      }

      return null;
    },
  }
);

const siteNav = css`
  height: ${toRem(presets.baselineGrid * 4)};
  display: flex;
  align-items: center;
`;

const navLinks = css`
  display: block;
  position: relative;
  line-height: ${toRem(presets.baselineGrid * 3)};
  padding: ${linksPadding.top} ${linksPadding.left};
  font-weight: 600;
  text-decoration: none;
  color: ${colors.gray.copy};
  opacity: 0.86;
  transition: all ${speed.default} ${curve.default};

  .dark-header .headroom--unfixed & {
    color: ${colors.ui.bright};
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    left: ${linksPadding.left};
    right: ${linksPadding.right};
    bottom: ${linksPadding.bottom};
    height: ${presets.baselineGrid}px;
    background-color: ${colors.accent.green};
    z-index: -1;
    opacity: 1;
    transform: scale(0.86, 0);
    transform-origin: center bottom;
    transition: opacity ${speed.fast} ${curve.default}, transform ${speed.default} ${curve.default};
    transition-delay: 100ms;
    pointer-events: none;
  }

  &.is-active {
    opacity: 1;
    cursor: default;
  }

  &:not(.is-active) {
    &:hover,
    &:active {
      opacity: 1;

      &::before {
        opacity: 0.72;
        transform: scale(1, 1);
        transition-delay: 0ms;
      }
    }
  }
`;

const NavItem = ({ linkTo, children }) => (
  <div css={navItem}>
    <Link to={linkTo} activeClassName="is-active" exact css={navLinks}>
      {children}
    </Link>
  </div>
);

NavItem.propTypes = {
  linkTo: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const Navigation = () => (
  <nav css={siteNav}>
    <MenuToggle
      label="Navigation Menu"
      color="currentColor"
      onClick={() => {
        events.trigger('navToggleClicked');
      }}
      styles={navToggle}
    />

    {navItems.sort((a, b) => a.order - b.order).map((item, index) => (
      <NavItem key={index} linkTo={item.path}>
        {item.label}
      </NavItem>
    ))}
  </nav>
);

export default Navigation;



// WEBPACK FOOTER //
// ./src/components/navigation.js